// src/features/cyber-hub/graphql/queries.ts
import { gql } from '@apollo/client';

export const GET_QUALITATIVE_FACTORS = gql`
  query GetQualitativeFactors($locationUUID: ID) {
    getQualitativeFactorsMetrics(locationUUID: $locationUUID) {
      factorsCount
      applicationsCount
      isEnabled
    }
  }
`;

export const GET_PROCESS_AREAS = gql`
  query GetProcessAreas($locationUUID: ID) {
    getProcessAreasMetrics(locationUUID: $locationUUID) {
      processAreasCount
      applicationsCount
      isEnabled
    }
  }
`;

export const GET_DRP_IT_APPLICATIONS_SUMMARY = gql`
  query GetItApplicationsSummary($locationUUID: ID) {
    getItApplicationsDashboard(locationUUID: $locationUUID) {
      cards {
        applicationsMapped {
          count
          isEnabled
        }
      }
      applications {
        name
        drpLink
        tier {
          color
          name
        }
      }
    }
  }
`;

export const GET_BUSINESS_FUNCTIONS_IT_APPLICATION_SUMMARY = gql`
  query GetBusinessFunctionsItApplicationSummary($locationUUID: ID) {
    getBusinessFunctionsItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      applicationsCount
    }
  }
`;

export const GET_QUALITATIVE_FACTORS_IT_APPLICATION_SUMMARY = gql`
  query GetQualitativeFactorsItApplicationSummary($locationUUID: ID) {
    getQualitativeFactorsItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      applicationsCount
    }
  }
`;

export const GET_BUSINESS_FUNCTIONS_IT_APPLICATION_REPORT = gql`
  query GetBusinessFunctionItApplicationSummary($locationUUID: ID) {
    getBusinessFunctionItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      totalTaggedApplications
      applicationsWithWorkarounds
      completionPercentage
      workaroundCompliancePercentage {
        sla
        rto
        mto
        rpo
      }
    }
  }
`;
export const GET_QUALITATIVE_FACTOR_IT_APPLICATION_SUMMARY = gql`
  query GetQualitativeFactorItApplicationSummary($locationUUID: ID) {
    getQualitativeFactorItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      totalTaggedApplications
      completionPercentage
      workaroundCompliancePercentage {
        sla
        rto
        mto
        rpo
      }
    }
  }
`;

export const GET_PROCESS_AREAS_IT_APPLICATION_REPORT = gql`
  query GetProcessAreasItApplicationSummary($locationUUID: ID!) {
    getProcessAreasItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      totalTaggedApplications
      completionPercentage
      workaroundCompliancePercentage {
        sla
        rto
        mto
        rpo
      }
    }
  }
`;

export const GET_PROCESS_AREAS_IT_APPLICATION_SUMMARY = gql`
  query GetProcessAreasItApplicationSummary($locationUUID: ID) {
    getProcessAreaItApplicationSummary(locationUUID: $locationUUID) {
      uuid
      name
      applicationsCount
    }
  }
`;

export const GET_IT_APPLICATIONS_WORKAROUND_COMPLIANCE = gql`
  query GetItApplicationsWorkaroundCompliance($locationUUID: ID) {
    getITApplicationsWorkaroundCompliance(locationUUID: $locationUUID) {
      metric
      percentage
      count
    }
  }
`;

export const GET_ALL_IT_APPLICATIONS_METRICS = gql`
  query GetAllItApplicationsMetrics($data: FilterInput) {
    findAllItApplications(data: $data) {
      uuid
    }
  }
`;

export const GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_LOCATION_METRICS = gql`
  query GetDirectlySupportedItApplicationsLocationMetrics($data: ID!) {
    getSelectedLocationItApplications(data: $data) {
      uuid
    }
  }
`;

export const GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_ENTERPRISE_METRICS = gql`
  query GetDirectlySupportedItApplicationsEnterpriseMetrics(
    $data: FilterInput
  ) {
    getSelectedItApplications(data: $data) {
      uuid
    }
  }
`;

export const GET_IT_APPLICATIONS_METRICS = gql`
  query GetItApplicationsMetrics($locationUUID: ID) {
    getItApplicationsMetrics(locationUUID: $locationUUID) {
      applicationCount
      mappedApplicationsCount
      unmappedApplicationsCount
      revenueMappedApplicationsCount
    }
  }
`;

export const GET_BUSINESS_FUNCTIONS_METRICS = gql`
  query GetBusinessFunctionsMetrics($locationUUID: ID) {
    getBusinessFunctionsMetrics(locationUUID: $locationUUID) {
      functionsCount
      uniqueApplicationsCount
    }
  }
`;

export const GET_LOCATIONS_IT_APPLICATION_SUMMARY = gql`
  query GetLocationsItApplicationSummary {
    getLocationsItApplicationSummary {
      uuid
      name
      applicationsCount
    }
  }
`;

export const GET_BUSINESS_FUNCTIONS_WITH_IT_APPLICATIONS = gql`
  query GetBusinessFunctionsWithItApplications(
    $businessFunctionUUID: ID!
    $locationUUID: ID
  ) {
    getBusinessFunctionsWithItApplications(
      businessFunctionUUID: $businessFunctionUUID
      locationUUID: $locationUUID
    ) {
      uuid
      name
      itApplications {
        uuid
        name
        applicationType
        tier {
          uuid
          name
          color
          description
          keyFeatures
          riskLevel
          cost
        }
        sla
        rto
        mto
        rpo
        completionPercentage
        comment
        workaround {
          isWorkaroundNeeded
          workaroundDefined
          runtimeHours
          maxDataLossHours
          workaroundDescription
          workaroundUpdatedAt
          workaroundTimelineCompliance {
            slaCompliant
            rtoCompliant
            mtoCompliant
            rpoCompliant
          }
        }
      }
    }
  }
`;

export const GET_QUALITATIVE_FACTORS_WITH_IT_APPLICATIONS = gql`
  query GetQualitativeFactorsWithItApplications(
    $qualitativeFactorUUID: ID!
    $locationUUID: ID
  ) {
    getQualitativeFactorsWithItApplications(
      qualitativeFactorUUID: $qualitativeFactorUUID
      locationUUID: $locationUUID
    ) {
      uuid
      name
      itApplications {
        uuid
        name
        applicationType
        tier {
          uuid
          name
          color
          description
          keyFeatures
          riskLevel
          cost
        }
        sla
        rto
        mto
        rpo
        completionPercentage
        comment
        workaround {
          isWorkaroundNeeded
          workaroundDefined
          runtimeHours
          maxDataLossHours
          workaroundDescription
          workaroundUpdatedAt
          workaroundTimelineCompliance {
            slaCompliant
            rtoCompliant
            mtoCompliant
            rpoCompliant
          }
        }
      }
    }
  }
`;

export const GET_PROCESS_AREAS_WITH_IT_APPLICATIONS = gql`
  query GetProcessAreasWithItApplications(
    $processAreaUUID: ID!
    $locationUUID: ID
  ) {
    getProcessAreasWithItApplications(
      processAreaUUID: $processAreaUUID
      locationUUID: $locationUUID
    ) {
      uuid
      name
      itApplications {
        uuid
        name
        applicationType
        tier {
          uuid
          name
          color
          description
          keyFeatures
          riskLevel
          cost
        }
        sla
        rto
        mto
        rpo
        completionPercentage
        comment
        workaround {
          isWorkaroundNeeded
          workaroundDefined
          runtimeHours
          maxDataLossHours
          workaroundDescription
          workaroundUpdatedAt
          workaroundTimelineCompliance {
            slaCompliant
            rtoCompliant
            mtoCompliant
            rpoCompliant
          }
        }
      }
    }
  }
`;

export const GET_APPLICATION_REPORT = gql`
  query GetApplicationReport($applicationUUID: ID!, $locationUUID: ID) {
    getApplicationReport(
      applicationUUID: $applicationUUID
      locationUUID: $locationUUID
    ) {
      drpReport {
        drpInPlace
        drpLink
      }
      revenueSupported
      businessFunctionsSupported {
        total
        supported
      }
      processAreasSupported {
        total
        supported
      }
      qualitativeFactorsSupported {
        total
        supported
      }
    }
  }
`;

export const GET_IT_APPLICATION_METRICS = gql`
  query GetItApplicationMetrics($locationUUID: ID, $includeUnmapped: Boolean) {
    getItApplicationMetrics(
      locationUUID: $locationUUID
      includeUnmapped: $includeUnmapped
    ) {
      uuid
      name
      applicationType
      tier {
        uuid
        name
        color
      }
      slaRatio
      rtoRatio
      mtoRatio
      rpoRatio
      annualRevenue
      supportedBusinessFunctions
      drpInPlace
      unmapped
      qualitativeFactors {
        reputation
        employees
        lifeSafety
        compliance
        count
      }
    }
  }
`;

export const GET_IT_APPLICATION_REPORT_OVERVIEW = gql`
  query GetItApplicationReportOverview(
    $applicationUUID: ID!
    $locationUUID: ID
  ) {
    getItApplicationReportOverview(
      applicationUUID: $applicationUUID
      locationUUID: $locationUUID
    ) {
      hostingEntity
      tier {
        name
        color
      }
      applicationType
      description
      sla
      rto
      mto
      rpo
    }
  }
`;

// Centralized from components/ApplicationFilters.tsx
export const GET_TIER_OPTIONS = gql`
  query GetTierOptions {
    getTiers {
      uuid
      name
      color
    }
  }
`;
